import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';

import { useProductSearch } from '../../hooks';
import { formatPrice } from '../../lib/format';
import type { ProductRef } from '../../model/types';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

export interface ProductAutocompleteProps {
  value: ProductRef[];
  onChange: (value: ProductRef[]) => void;
  label: string;
  disabled?: boolean;
  helperText?: string;
}

/**
 * Async multi-select product search (plan-011's "no-UUID centerpiece control") — debounced,
 * min-2-chars, options carry name + SKU + price. Copies the BFF's product-search endpoint shape
 * (see `api/refApi.ts`); FSD forbids reusing `features/catalog`'s own product search hook.
 */
export function ProductAutocomplete({
  value,
  onChange,
  label,
  disabled = false,
  helperText,
}: ProductAutocompleteProps) {
  const { t } = useTranslation('promotions');
  const [inputValue, setInputValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const query = useProductSearch(
    { query: debounced, size: 20 },
    { enabled: debounced.trim().length >= MIN_QUERY_LENGTH },
  );
  const options = query.data?.content ?? [];
  const loading = query.isFetching;

  const scheduleSearch = (text: string) => {
    if (timer) clearTimeout(timer);
    setTimer(setTimeout(() => setDebounced(text), DEBOUNCE_MS));
  };

  return (
    <Autocomplete<ProductRef, true, false, false>
      multiple
      fullWidth
      disabled={disabled}
      value={value}
      inputValue={inputValue}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(option) => option.name}
      noOptionsText={
        inputValue.trim().length < MIN_QUERY_LENGTH
          ? t('productAutocomplete.minLengthHint')
          : t('productAutocomplete.noOptions')
      }
      loadingText={t('productAutocomplete.loading')}
      onInputChange={(_event, newValue, reason) => {
        if (reason === 'input') {
          setInputValue(newValue);
          scheduleSearch(newValue);
        }
        if (reason === 'clear') {
          setInputValue('');
          setDebounced('');
        }
      }}
      onChange={(_event, newValue) => onChange(newValue)}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip size="small" label={option.name} key={key} {...tagProps} />;
        })
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Box sx={{ py: 0.25, minWidth: 0, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" noWrap>
                  {option.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 }}
                >
                  {formatPrice(option.price)}
                </Typography>
              </Box>
              {option.code ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {option.code}
                </Typography>
              ) : null}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={t('productAutocomplete.placeholder')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
